import {
  clampCookieIconShadow,
  cookieIconBorderRadiusCss,
  cookieIcon3dButtonBackground,
  cookieIcon3dButtonShadow,
  cookieIcon3dButtonHoverShadow,
} from './cookieIcon3dButton';
import {
  isCookieIconSelfColored,
  cookieIconUsesButtonPlate,
} from './cookieIconGallery';

function hex2rgb(hex) {
  hex = hex.replace('#', '');
  if (hex.length === 3) hex = hex[0]+hex[0]+hex[1]+hex[1]+hex[2]+hex[2];
  const n = parseInt(hex, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function luminance(hex) {
  const [r, g, b] = hex2rgb(hex);
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

function luminanceFromRgbTriplet(rgb) {
  const parts = String(rgb || '')
    .split(',')
    .map((s) => parseInt(s.trim(), 10));
  if (parts.length < 3 || parts.some((n) => Number.isNaN(n))) return 0;
  return 0.299 * parts[0] + 0.587 * parts[1] + 0.114 * parts[2];
}

function contrastTextForBackground(bg) {
  const lum =
    typeof bg === 'string' && bg.includes(',')
      ? luminanceFromRgbTriplet(bg)
      : luminance(bg);
  return lum > 128 ? '#222222' : '#ffffff';
}

function contrastTextRgbTriplet(bg) {
  return hex2rgb(contrastTextForBackground(bg)).join(', ');
}

function darken(hex, amount) {
  return 'rgb(' + hex2rgb(hex).map(v => Math.max(0, v - amount)).join(',') + ')';
}

function normalizeHex(color) {
  if (!color || typeof color !== 'string') return null;
  let h = color.trim().replace('#', '');
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  return /^[0-9a-fA-F]{6}$/.test(h) ? h.toLowerCase() : null;
}

function wcagRatio(fgHex, bgHex) {
  const lum = (h) => {
    const [r, g, b] = hex2rgb('#' + h).map((c) => {
      const s = c / 255;
      return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };
  const l1 = lum(fgHex);
  const l2 = lum(bgHex);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

/** WCAG AA 4.5:1 for Accept button text (fixes #fff on #22c55e = 2.27:1). */
function ensureAcceptContrast(text, bg) {
  const fgHex = normalizeHex(text) || 'ffffff';
  const bgHex = normalizeHex(bg) || '22c55e';
  if (wcagRatio(fgHex, bgHex) >= 4.5) {
    return { fg: '#' + fgHex, bg: '#' + bgHex };
  }
  const [r, g, b] = hex2rgb('#' + bgHex);
  for (let step = 8; step <= 200; step += 8) {
    const candidate = [r, g, b]
      .map((v) => Math.max(0, v - step).toString(16).padStart(2, '0'))
      .join('');
    if (wcagRatio(fgHex, candidate) >= 4.5) {
      return { fg: '#' + fgHex, bg: '#' + candidate };
    }
  }
  return { fg: '#ffffff', bg: '#15803d' };
}

/**
 * The banner is appended to `document.body`, which lives outside any OS 2.0
 * `.color-scheme-*` wrapper. If we used `var(--color-background)` the banner
 * could inherit the scheme of whatever section it overlapped — e.g. a white
 * featured-collection wrapper on an otherwise dark theme, producing a white
 * banner on a dark store. Source of truth instead: the admin action resolves
 * the active theme's PRIMARY scheme from `settings_data.json` and persists
 * the RGB triplets into the metafield as `theme_background_rgb` /
 * `theme_foreground_rgb`. The Liquid embed inlines them into
 * `window.accessProCookieBannerSettings` so we read them here as solid hex/rgb
 * colors — no `var()` lookup, no section-level leakage.
 */
/** Match admin LivePreview mobileFs ≈ font_size * 0.72 */
function mapMobileFontSize(desktopPx, ratio = 0.72, minPx = 10) {
  const base = Number(desktopPx) || 18;
  return `${Math.max(minPx, Math.round(base * ratio))}px`;
}

export function buildCSS(c, BANNER_ID, PREFS_ID, OVERLAY_ID, COOKIE_ICON_ID = 'acb-cookie-icon') {
  const matchTheme = c.color_scheme === 'match_theme';

  const themeBgRgb =
    (typeof c.theme_background_rgb === 'string' && c.theme_background_rgb.trim()) ||
    '255, 255, 255';
  const themeFgRgb = matchTheme
    ? contrastTextRgbTriplet(themeBgRgb)
    : (typeof c.theme_foreground_rgb === 'string' && c.theme_foreground_rgb.trim()) ||
      '26, 26, 26';
  const themeBg = `rgb(${themeBgRgb})`;
  const themeFg = `rgb(${themeFgRgb})`;

  const baseBg = matchTheme ? themeBg : (c.bg_color || '#ffffff');
  const fg = matchTheme ? contrastTextForBackground(themeBgRgb) : (c.text_color || '#222222');

  // Solid two-layer surface: a ~12% foreground tint over the theme background.
  // The tint gives the banner the look of an elevated card so it stays
  // distinct from the page even when the page is the same theme color. Both
  // layers are fully opaque gradients so the surface can never appear
  // translucent regardless of which page section sits under it.
  const matchThemeSurface =
    `linear-gradient(rgba(${themeFgRgb}, 0.12), rgba(${themeFgRgb}, 0.12)),` +
    ` linear-gradient(${themeBg}, ${themeBg})`;
  const surfaceShorthand = matchTheme ? matchThemeSurface : baseBg;
  /** First-layer banner in admin preview uses solid theme bg (not the tinted card surface). */
  const bannerSurface = matchTheme ? themeBg : surfaceShorthand;
  const bg = baseBg;
  const fontRule = c.font_family ? `font-family:${c.font_family};` : '';
  const desktopFontSize = c.font_size || 18;
  const fontSize = `${desktopFontSize}px`;
  const mobileBannerFontSize = mapMobileFontSize(desktopFontSize);
  const mobilePrefsFontSize = mapMobileFontSize(desktopFontSize);
  let border;
  let mutedBg;
  let toggleOff;
  let isLight = false;
  if (matchTheme) {
    border = `rgba(${themeFgRgb}, 0.20)`;
    mutedBg = `rgba(${themeFgRgb}, 0.08)`;
    toggleOff = `rgba(${themeFgRgb}, 0.28)`;
    const bgParts = themeBgRgb.split(',').map((s) => parseInt(s.trim(), 10));
    if (bgParts.length >= 3 && bgParts.every((n) => !Number.isNaN(n))) {
      isLight = 0.299 * bgParts[0] + 0.587 * bgParts[1] + 0.114 * bgParts[2] > 128;
    }
  } else {
    isLight = luminance(bg) > 128;
    border = isLight ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)';
    mutedBg = isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.08)';
    toggleOff = isLight ? '#d1d5db' : '#374151';
  }
  // Wix classic defaults — Accept blue, ghost Reject/Prefs on light.
  const acceptBg = c.button_accept_bg || '#2563eb';
  const acceptText = c.button_accept_text || '#ffffff';
  const prefsBg = (c.button_prefs_bg != null && c.button_prefs_bg !== '')
    ? c.button_prefs_bg
    : 'transparent';
  const prefsBorder = c.button_prefs_border || (isLight ? '#6b7280' : border);
  let rejectBg = (c.button_reject_bg != null && c.button_reject_bg !== '')
    ? c.button_reject_bg
    : (matchTheme ? mutedBg : (isLight ? 'transparent' : '#32324a'));
  if (
    !matchTheme &&
    !isLight &&
    (!rejectBg || rejectBg === 'transparent' || rejectBg === 'rgba(0,0,0,0)')
  ) {
    rejectBg = '#32324a';
  }
  const rejectIsGhost = !rejectBg || rejectBg === 'transparent' || rejectBg === 'rgba(0,0,0,0)';
  const isNearWhiteHex = (color) => {
    if (!color || typeof color !== 'string' || !color.trim().startsWith('#')) return false;
    return luminance(color.trim()) > 200;
  };
  const isTransparentBg = (value) =>
    !value || value === 'transparent' || value === 'rgba(0,0,0,0)';
  const outlineDarkLabel = matchTheme ? fg : '#222222';
  const outlineFallback = matchTheme ? fg : (isLight ? '#222222' : '#ffffff');
  const resolveOutlineText = (stored) => {
    if (stored && isNearWhiteHex(stored) && (matchTheme || isLight)) return outlineDarkLabel;
    return stored || outlineFallback;
  };
  const prefsText = isTransparentBg(prefsBg)
    ? resolveOutlineText(c.button_prefs_text)
    : (c.button_prefs_text || outlineFallback);
  const rejectText = rejectIsGhost
    ? resolveOutlineText(c.button_reject_text)
    : (c.button_reject_text || outlineFallback);
  const rejectBorderRule = rejectIsGhost
    ? `border:1px solid ${prefsBorder};padding:8px 17px;box-sizing:border-box;`
    : 'border:none;';
  const dnsBg = c.button_dns_bg || mutedBg;
  const dnsText = c.button_dns_text || fg;
  const cookieIconBg =
    (typeof c.cookie_icon_bg_color === 'string' && c.cookie_icon_bg_color.trim()) ||
    '#2563eb';
  const cookieIconFg =
    (typeof c.cookie_icon_color === 'string' && c.cookie_icon_color.trim()) ||
    '#ffffff';
  const cookieIconSelfColored = isCookieIconSelfColored(c.cookie_icon_style);
  const cookieIconUsePlate = cookieIconUsesButtonPlate(c.cookie_icon_style, c);
  const cookieIconRadius = cookieIconUsePlate
    ? cookieIconBorderRadiusCss(c.cookie_icon_radius)
    : '50%';
  const cookieIconShadowAmount = clampCookieIconShadow(c.cookie_icon_shadow);
  const cookieIconBg3d = cookieIconUsePlate
    ? cookieIcon3dButtonBackground(cookieIconBg, cookieIconShadowAmount)
    : 'transparent';
  const cookieIconShadow = cookieIconUsePlate
    ? cookieIcon3dButtonShadow(false, cookieIconShadowAmount)
    : 'none';
  const cookieIconHoverShadow = cookieIconUsePlate
    ? cookieIcon3dButtonHoverShadow(false, cookieIconShadowAmount)
    : 'none';
  // Text reopen always uses the saved glyph/text color — never inherit from
  // a previously selected self-colored icon style (which would read as black).
  const cookieIconFgRule =
    c.cookie_icon_display === 'text'
      ? cookieIconFg
      : cookieIconSelfColored
        ? 'inherit'
        : cookieIconFg;

  return `
#${OVERLAY_ID}{position:fixed;inset:0;background:rgba(0,0,0,0.55);z-index:2147483645;transition:opacity .3s;}
#${OVERLAY_ID}.acb-hiding{opacity:0;pointer-events:none;}

#${BANNER_ID},#${PREFS_ID}{all:initial;box-sizing:border-box;position:fixed;z-index:2147483646;background:${surfaceShorthand};color:${fg};font-size:${fontSize};line-height:1.55;${fontRule}font-weight:400;transition:opacity .3s,transform .3s;opacity:0;pointer-events:none;}
#${BANNER_ID}{background:${bannerSurface};}
#${BANNER_ID} *,#${PREFS_ID} *{box-sizing:border-box;}
#${BANNER_ID}.acb-visible,#${PREFS_ID}.acb-visible{opacity:1;pointer-events:auto;}

/* Classic bar — full-bleed, roomier padding (aligned with admin LivePreview). */
#${BANNER_ID}.acb-layout-banner{left:0;right:0;padding:14px 20px;border:none;border-radius:0;backdrop-filter:none;-webkit-backdrop-filter:none;}
/* Soft elevation only — no inset edge highlight. */
#${BANNER_ID}.acb-layout-banner.acb-pos-bottom{bottom:0;transform:translateY(0);box-shadow:0 -12px 32px rgba(0,0,0,0.28),0 -2px 6px rgba(0,0,0,0.18);}
#${BANNER_ID}.acb-layout-banner.acb-pos-top{top:0;transform:translateY(0);box-shadow:0 12px 32px rgba(0,0,0,0.28),0 2px 6px rgba(0,0,0,0.18);}
@media(max-width:640px){#${BANNER_ID}{font-size:${mobileBannerFontSize};}#${BANNER_ID}.acb-layout-banner{left:0;right:0;border-radius:0;padding:14px 20px;}#${BANNER_ID}.acb-layout-banner.acb-pos-bottom{bottom:0;}#${BANNER_ID}.acb-layout-banner.acb-pos-top{top:0;}}
@media(min-width:641px){#${BANNER_ID}.acb-layout-banner{padding:18px 28px;}}

#${BANNER_ID}.acb-layout-dialog{top:50%;left:50%;transform:translate(-50%,-46%);width:min(540px,90vw);border-radius:10px;padding:28px;border:1px solid ${border};box-shadow:0 24px 64px rgba(0,0,0,0.45),0 0 0 1px rgba(${themeFgRgb}, 0.08);backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);}
#${BANNER_ID}.acb-layout-dialog.acb-visible{transform:translate(-50%,-50%);}

#${BANNER_ID}.acb-layout-box{width:min(340px,90vw);border-radius:8px;padding:20px;bottom:20px;border:1px solid ${border};box-shadow:0 12px 32px rgba(0,0,0,0.36),0 2px 6px rgba(0,0,0,0.18);transform:translateY(16px);backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);}
#${BANNER_ID}.acb-layout-box.acb-pos-left{left:20px;}
#${BANNER_ID}.acb-layout-box.acb-pos-right{right:20px;}
#${BANNER_ID}.acb-layout-box.acb-visible{transform:translateY(0);}
@media(max-width:640px){#${BANNER_ID}.acb-layout-box{width:min(340px,74vw);bottom:12px;padding:14px 12px;}#${BANNER_ID}.acb-layout-box.acb-pos-left{left:12px;right:auto;}#${BANNER_ID}.acb-layout-box.acb-pos-right{right:12px;left:auto;}#${BANNER_ID}.acb-layout-dialog{padding:16px 14px;width:min(540px,88vw);border-radius:12px;}}

.acb-inner{display:flex;flex-direction:column;gap:12px;}
/* Classic banner — text left, equal-width actions right on desktop. */
.acb-layout-banner .acb-inner{flex-direction:row;align-items:flex-end;justify-content:space-between;gap:24px;flex-wrap:nowrap;}
@media(min-width:641px){.acb-layout-banner .acb-inner{gap:50px;align-items:flex-end;}}
@media(max-width:640px){.acb-layout-banner .acb-inner{flex-direction:column;align-items:stretch;gap:10px;}}
.acb-text-block{flex:1;min-width:0;}
.acb-heading{margin:0 0 8px;font-weight:700;font-size:1.15em;line-height:1.25;}
.acb-layout-banner .acb-heading{display:block;margin:0 0 10px;font-weight:700;font-size:1.05em;line-height:1.3;letter-spacing:-0.01em;}
@media(min-width:641px){.acb-layout-banner .acb-heading{margin:0 0 12px;font-size:1.125em;}}
.acb-body{margin:0;opacity:1;font-size:0.96em;line-height:1.55;}
.acb-layout-banner .acb-body{opacity:1;font-size:0.96em;line-height:1.5;}
@media(min-width:641px){.acb-layout-banner .acb-body{line-height:1.55;}}
.acb-layout-banner .acb-body a,.acb-body a{color:inherit;text-decoration:underline;cursor:pointer;}
.acb-links{margin-top:6px;display:flex;gap:10px;flex-wrap:wrap;}
.acb-links a{color:${fg};opacity:.7;text-decoration:underline;cursor:pointer;font-size:.88em;}
.acb-links a:hover{opacity:1;}

.acb-buttons{display:flex;gap:8px;flex-wrap:wrap;flex-shrink:0;align-items:center;}
/* Classic banner — equal-width action grid. */
.acb-layout-banner .acb-buttons{display:grid!important;grid-auto-flow:column;grid-auto-columns:1fr;justify-content:flex-end;gap:12px;width:auto;flex-shrink:0;align-items:stretch;padding-bottom:8px;}
.acb-layout-banner .acb-btn{width:100%!important;min-width:0;box-sizing:border-box!important;padding:10px 18px;border-radius:3px;font-size:.95em;font-weight:500;text-align:center;}
.acb-layout-banner .acb-btn-accept{font-weight:500;}
.acb-layout-banner .acb-btn-prefs{padding:9px 17px;border:1.8px solid ${prefsBorder}!important;box-sizing:border-box!important;}
@media(min-width:641px){.acb-layout-banner .acb-btn{padding:11px 20px!important;border-radius:3px!important;font-size:.95em!important;font-weight:500;min-height:42px;text-align:center;}.acb-layout-banner .acb-btn-prefs{padding:10px 19px!important;}}
@media(max-width:640px){.acb-layout-banner .acb-buttons{width:100%;}.acb-layout-banner .acb-btn-prefs{border:1px solid ${prefsBorder}!important;padding:8px 14px!important;}}
/* Classic box — 3 stacked rows: Accept on top, Reject last. */
.acb-layout-box .acb-buttons{flex-direction:column-reverse;align-items:stretch;width:100%;gap:8px;justify-content:flex-end;}
.acb-layout-box .acb-btn{width:100%;border-radius:3px;padding:12px 14px;min-height:40px;box-sizing:border-box;}
/* Classic dialog — equal-width row actions. */
.acb-layout-dialog .acb-buttons{justify-content:stretch!important;gap:8px;width:100%;align-items:stretch;}
.acb-layout-dialog .acb-btn{flex:1 1 0!important;min-width:0!important;width:auto!important;border-radius:3px!important;padding:11px 20px!important;font-weight:500!important;text-align:center;font-size:.95em!important;box-sizing:border-box!important;min-height:42px;white-space:nowrap;}
@media(max-width:480px){.acb-layout-box .acb-btn{padding:10px 12px!important;min-height:36px;font-size:.9em!important;white-space:normal!important;}.acb-layout-dialog .acb-buttons{flex-direction:column!important;flex-wrap:nowrap!important;align-items:stretch!important;}.acb-layout-dialog .acb-btn{flex:none!important;width:100%!important;min-width:0!important;min-height:0;padding:10px 12px!important;font-size:.9em!important;white-space:normal!important;}.acb-layout-dialog .acb-btn-prefs{padding:10px 12px!important;}}

.acb-btn{all:initial;display:inline-flex;align-items:center;justify-content:center;padding:9px 18px;border-radius:3px;cursor:pointer;font-size:.9em;font-weight:500;line-height:1;white-space:nowrap;transition:opacity .15s,transform .1s;${fontRule}}
.acb-btn:hover{opacity:.85;}
.acb-btn:active{transform:scale(.97);}
/* No visible focus rings on click — storefront themes + :focus-visible both paint blue outlines. */
.acb-btn:focus,.acb-btn:focus-visible{outline:none!important;box-shadow:none!important;}
#${BANNER_ID}:focus,#${PREFS_ID}:focus{outline:none;}
.acb-toggle input:focus + .acb-toggle-track,
.acb-toggle input:focus-visible + .acb-toggle-track{outline:none!important;box-shadow:none!important;}
.acb-links a:focus,.acb-links a:focus-visible{outline:none!important;}
.acb-layout-banner .acb-body a:focus,.acb-layout-banner .acb-body a:focus-visible,
.acb-body a:focus,.acb-body a:focus-visible{outline:none!important;}
.acb-btn-accept{background:${acceptBg};color:${acceptText};}
.acb-btn-reject{background:${rejectBg};color:${rejectText};${rejectBorderRule}}
.acb-btn-prefs{background:${prefsBg};color:${prefsText};border:1px solid ${prefsBorder};padding:8px 17px;}
.acb-btn-dns{background:${dnsBg};color:${dnsText};font-size:.85em;}
.acb-btn-dismiss{background:${rejectBg};color:${rejectText};${rejectBorderRule}}

#${PREFS_ID}{top:50%;left:50%;transform:translate(-50%,-46%);width:min(560px,80vw);max-height:88vh;overflow-y:auto;border-radius:16px;padding:28px 24px 24px;border:none;box-shadow:0 24px 64px rgba(0,0,0,0.55),0 0 0 1px rgba(${themeFgRgb}, 0.08);backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);}
#${PREFS_ID}.acb-visible{transform:translate(-50%,-50%);}

/* Icon-anchored preferences (same content; not the centered middle popup). */
#${PREFS_ID}.acb-prefs-anchored{top:auto;left:auto;right:auto;bottom:auto;width:min(360px,calc(100vw - 24px));max-height:min(70vh,calc(100vh - 100px));border-radius:12px;padding:20px 18px 16px;box-shadow:0 12px 32px rgba(0,0,0,0.36),0 2px 6px rgba(0,0,0,0.18);transform:translateY(8px);}
#${PREFS_ID}.acb-prefs-anchored.acb-visible{transform:none;}
#${PREFS_ID}.acb-prefs-anchored.acb-prefs-pos-bottom-left{bottom:76px;left:20px;}
#${PREFS_ID}.acb-prefs-anchored.acb-prefs-pos-bottom-right{bottom:76px;right:20px;}
#${PREFS_ID}.acb-prefs-anchored.acb-prefs-pos-top-left{top:76px;left:20px;transform:translateY(-8px);}
#${PREFS_ID}.acb-prefs-anchored.acb-prefs-pos-top-right{top:76px;right:20px;transform:translateY(-8px);}
#${PREFS_ID}.acb-prefs-anchored.acb-prefs-pos-top-left.acb-visible,#${PREFS_ID}.acb-prefs-anchored.acb-prefs-pos-top-right.acb-visible{transform:none;}
@media(max-width:640px){
#${PREFS_ID}.acb-prefs-anchored{width:auto;left:14px;right:14px;}
#${PREFS_ID}.acb-prefs-anchored.acb-prefs-pos-bottom-left,#${PREFS_ID}.acb-prefs-anchored.acb-prefs-pos-bottom-right{bottom:70px;left:14px;right:14px;}
#${PREFS_ID}.acb-prefs-anchored.acb-prefs-pos-top-left,#${PREFS_ID}.acb-prefs-anchored.acb-prefs-pos-top-right{top:70px;left:14px;right:14px;}
}

/* Icon-only “Your cookie settings” status panel — same colors as preferences. */
#${PREFS_ID}.acb-cookie-settings{
  background:${surfaceShorthand};
  color:${fg};
  width:min(340px,calc(100vw - 24px));
  padding:0;
  border:1px solid ${border};
  border-radius:12px;
  box-shadow:0 12px 32px rgba(0,0,0,0.36),0 2px 6px rgba(0,0,0,0.18);
  overflow:hidden;
}
.acb-settings-header{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  padding:16px 18px 14px;
  border-bottom:1px solid ${border};
}
.acb-settings-title{
  margin:0;
  font-weight:700;
  font-size:1.05em;
  line-height:1.3;
  color:${fg};
}
.acb-settings-close{
  all:initial;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  width:28px;
  height:28px;
  margin:0;
  padding:0;
  cursor:pointer;
  color:${fg};
  border-radius:6px;
  flex-shrink:0;
  ${fontRule}
}
.acb-settings-close:hover{background:${mutedBg};}
.acb-settings-close:focus,
.acb-settings-close:focus-visible{outline:none!important;box-shadow:none!important;}
.acb-settings-body{padding:16px 18px 8px;}
.acb-settings-state-label{
  margin:0 0 10px;
  font-size:.95em;
  font-weight:400;
  color:${fg};
}
.acb-settings-status-list{
  list-style:none;
  margin:0 0 14px;
  padding:0;
  display:flex;
  flex-direction:column;
  gap:10px;
}
.acb-settings-status-item{
  display:flex;
  align-items:center;
  gap:8px;
  font-size:.92em;
  line-height:1.35;
  color:${fg};
}
.acb-settings-status-icon{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  width:16px;
  height:16px;
  flex-shrink:0;
}
.acb-settings-status-off{color:${fg};opacity:.85;}
.acb-settings-status-on{color:${acceptBg};}
.acb-settings-details-toggle{
  all:initial;
  display:inline-flex;
  align-items:center;
  gap:6px;
  margin:0 0 10px;
  padding:0;
  cursor:pointer;
  color:${fg};
  opacity:.85;
  font-size:.9em;
  line-height:1.3;
  ${fontRule}
}
.acb-settings-details-toggle:hover{opacity:1;text-decoration:underline;}
.acb-settings-chevron{display:block;transition:transform .15s;}
.acb-settings-details-toggle.acb-collapsed .acb-settings-chevron{transform:rotate(180deg);}
.acb-settings-details{
  background:${mutedBg};
  border-radius:8px;
  padding:12px 14px;
  margin:0 0 6px;
}
.acb-settings-details.acb-hidden{display:none;}
.acb-settings-details-row{
  margin:0;
  font-size:.88em;
  line-height:1.45;
  color:${fg};
  display:flex;
  flex-wrap:wrap;
  gap:6px;
}
.acb-settings-details-row strong{font-weight:700;}
.acb-settings-actions{
  display:flex;
  flex-direction:column;
  gap:10px;
  padding:8px 18px 18px;
}
#${PREFS_ID}.acb-cookie-settings .acb-btn{
  width:100%;
  border-radius:8px;
  padding:11px 16px;
  font-weight:600;
  font-size:.92em;
}
#${PREFS_ID}.acb-cookie-settings .acb-btn-withdraw{
  background:${prefsBg};
  color:${prefsText};
  border:1px solid ${prefsBorder};
}
#${PREFS_ID}.acb-cookie-settings .acb-btn-change{
  background:${acceptBg};
  color:${acceptText};
  border:1px solid ${acceptBg};
}

/* Classic preferences — absolute × close (dark on light / white on dark via fg). */
#${PREFS_ID} .acb-prefs-close{
  all:initial;
  position:absolute;
  top:20px;
  right:20px;
  cursor:pointer;
  font-size:1.45em;
  line-height:1;
  opacity:.7;
  padding:0 2px;
  color:${fg};
  font-family:inherit;
  z-index:1;
}
#${PREFS_ID} .acb-prefs-close:hover{opacity:1;}
#${PREFS_ID} .acb-prefs-close:focus,
#${PREFS_ID} .acb-prefs-close:focus-visible{outline:none!important;box-shadow:none!important;}
.acb-prefs-title{font-weight:700;font-size:1.1em;margin:0 0 8px;padding-right:28px;}
.acb-prefs-desc{opacity:.85;font-size:.9em;line-height:1.5;margin:0 0 16px;}
.acb-categories{border-top:1px solid ${border};}
.acb-category{padding:14px 0;border-bottom:1px solid ${border};}
.acb-cat-header{display:flex;align-items:center;justify-content:space-between;gap:12px;}
.acb-cat-title{font-weight:600;font-size:.95em;}
.acb-cat-desc{font-size:.85em;opacity:.8;margin:6px 0 0;line-height:1.45;}
.acb-always-on{font-size:.8em;opacity:.55;font-style:italic;flex-shrink:0;}
/* Classic prefs — category detail chevron (collapse / expand description). */
#${PREFS_ID} .acb-cat-details-toggle{
  all:unset;
  display:inline-flex;
  align-items:center;
  gap:6px;
  cursor:pointer;
  color:inherit;
  font:inherit;
  min-width:0;
}
#${PREFS_ID} .acb-cat-details-toggle:focus,
#${PREFS_ID} .acb-cat-details-toggle:focus-visible{
  outline:none!important;
  box-shadow:none!important;
}
#${PREFS_ID} .acb-cat-chevron{
  display:block;
  flex-shrink:0;
  opacity:.7;
  transition:transform .15s;
  transform:rotate(90deg);
}
#${PREFS_ID} .acb-category.acb-collapsed .acb-cat-chevron{transform:rotate(0deg);}
#${PREFS_ID} .acb-category.acb-collapsed .acb-cat-desc{display:none;}

.acb-toggle{position:relative;display:inline-block;width:44px;height:24px;flex-shrink:0;}
.acb-toggle input{opacity:0;width:0;height:0;position:absolute;}
.acb-toggle-track{position:absolute;inset:0;border-radius:12px;background:${toggleOff};transition:background .2s;cursor:pointer;}
.acb-toggle input:checked + .acb-toggle-track{background:${acceptBg};}
.acb-toggle-thumb{position:absolute;top:2px;left:2px;width:20px;height:20px;border-radius:50%;background:#fff;transition:transform .2s;pointer-events:none;}
.acb-toggle input:checked ~ .acb-toggle-thumb{transform:translateX(20px);}

/* Classic prefs — same equal-width 1st/2nd/3rd grid as classic banner. */
.acb-prefs-buttons{
  display:grid!important;
  grid-auto-flow:column;
  grid-auto-columns:1fr;
  gap:12px;
  margin-top:20px;
  width:100%;
  align-items:stretch;
  box-sizing:border-box;
}
#${PREFS_ID} .acb-prefs-buttons .acb-btn{
  width:100%!important;
  min-width:0;
  text-align:center;
  padding:14px 10px;
  border-radius:3px;
  box-sizing:border-box!important;
  font-weight:500;
}
@media(max-width:480px){
  #${PREFS_ID} .acb-prefs-buttons .acb-btn{padding:10px 8px!important;}
}
/* Classic prefs mobile — same mapping as Wix GDPR classic. */
@media(max-width:640px){
#${PREFS_ID}{font-size:${mobilePrefsFontSize};padding:16px 14px 14px;width:min(560px,calc(100vw - 24px));max-height:90vh;}
#${PREFS_ID}.acb-prefs-anchored{padding:16px 14px 14px;}
#${PREFS_ID} .acb-prefs-close{top:10px;right:12px;font-size:1.2em;}
.acb-prefs-title{font-size:1em;margin-bottom:6px;}
.acb-prefs-desc{font-size:.85em;margin-bottom:10px;}
.acb-category{padding:10px 0;}
.acb-cat-title{font-size:.9em;}
.acb-cat-desc{font-size:.8em;}
.acb-prefs-buttons{
  display:flex!important;
  margin-top:10px;
  gap:6px;
  flex-direction:column;
  align-items:stretch;
  width:100%;
  grid-auto-flow:unset;
  grid-auto-columns:unset;
}
#${PREFS_ID} .acb-prefs-buttons .acb-btn{
  width:100%!important;
  font-size:.82em;
  padding:10px 8px;
  border-radius:3px;
}
#${PREFS_ID} .acb-toggle{width:36px;height:20px;}
#${PREFS_ID} .acb-toggle-thumb{width:16px;height:16px;}
#${PREFS_ID} .acb-toggle input:checked ~ .acb-toggle-thumb{transform:translateX(16px);}
}
.acb-btn-save{background:${acceptBg};color:${acceptText};}
.acb-btn-reject-all{background:${rejectBg};color:${rejectText};${rejectBorderRule}}
.acb-btn-accept-all{background:${prefsBg};color:${prefsText};border:1px solid ${prefsBorder};}

#${COOKIE_ICON_ID}{all:initial;box-sizing:border-box;position:fixed;z-index:2147483646;width:48px;height:48px;border-radius:${cookieIconRadius};border:none;padding:0;margin:0;cursor:pointer;display:flex;align-items:center;justify-content:center;background:${cookieIconBg3d};color:${cookieIconFgRule};box-shadow:${cookieIconShadow};transition:transform .15s,box-shadow .15s;isolation:isolate;${fontRule}}
#${COOKIE_ICON_ID}:hover{transform:scale(1.06);box-shadow:${cookieIconHoverShadow};}
#${COOKIE_ICON_ID}:active{transform:scale(0.97);}
#${COOKIE_ICON_ID}:focus,
#${COOKIE_ICON_ID}:focus-visible{outline:none!important;box-shadow:${cookieIconShadow}!important;}
#${COOKIE_ICON_ID}{overflow:visible;}
#${COOKIE_ICON_ID} svg{position:relative;z-index:1;width:35px;height:35px;display:block;margin:0 auto;overflow:visible;pointer-events:none;}
#${COOKIE_ICON_ID}.acb-cookie-icon-size-small{width:40px;height:40px;}
#${COOKIE_ICON_ID}.acb-cookie-icon-size-small svg{width:30px;height:30px;}
#${COOKIE_ICON_ID}.acb-cookie-icon-size-medium{width:48px;height:48px;}
#${COOKIE_ICON_ID}.acb-cookie-icon-size-large{width:56px;height:56px;}
#${COOKIE_ICON_ID}.acb-cookie-icon-size-large svg{width:40px;height:40px;}
#${COOKIE_ICON_ID}.acb-cookie-icon-display-text,
#${COOKIE_ICON_ID}.acb-cookie-icon-display-text.acb-cookie-icon-size-small,
#${COOKIE_ICON_ID}.acb-cookie-icon-display-text.acb-cookie-icon-size-medium,
#${COOKIE_ICON_ID}.acb-cookie-icon-display-text.acb-cookie-icon-size-large{
  width:auto;
  height:auto;
  min-width:0;
  min-height:0;
  border-radius:4px 4px 0 0;
  background:${cookieIconBg};
  color:${cookieIconFg};
  -webkit-text-fill-color:${cookieIconFg};
  box-shadow:0 -1px 4px rgba(0,0,0,0.12);
  font-family:system-ui,-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;
  white-space:nowrap;
  line-height:1.2;
  font-weight:500;
}
#${COOKIE_ICON_ID}.acb-cookie-icon-display-text{padding:8px 14px;font-size:13px;}
#${COOKIE_ICON_ID}.acb-cookie-icon-display-text:hover{box-shadow:0 -1px 6px rgba(0,0,0,0.16);transform:none;}
#${COOKIE_ICON_ID}.acb-cookie-icon-display-text:active{transform:none;}
#${COOKIE_ICON_ID}.acb-cookie-icon-display-text.acb-cookie-icon-size-small{padding:6px 12px;font-size:12px;}
#${COOKIE_ICON_ID}.acb-cookie-icon-display-text.acb-cookie-icon-size-large{padding:10px 16px;font-size:15px;}
#${COOKIE_ICON_ID}.acb-cookie-icon-display-text svg{display:none;}
#${COOKIE_ICON_ID}.acb-cookie-icon-display-text.acb-cookie-icon-pos-bottom-left,
#${COOKIE_ICON_ID}.acb-cookie-icon-display-text.acb-cookie-icon-pos-top-left{
  top:auto;right:auto;bottom:0;left:10px;
}
#${COOKIE_ICON_ID}.acb-cookie-icon-display-text.acb-cookie-icon-pos-bottom-right,
#${COOKIE_ICON_ID}.acb-cookie-icon-display-text.acb-cookie-icon-pos-top-right{
  top:auto;left:auto;bottom:0;right:10px;
}
@media(max-width:640px){
#${COOKIE_ICON_ID}.acb-cookie-icon-display-text.acb-cookie-icon-pos-bottom-left,
#${COOKIE_ICON_ID}.acb-cookie-icon-display-text.acb-cookie-icon-pos-top-left{
  top:auto;right:auto;bottom:0;left:8px;
}
#${COOKIE_ICON_ID}.acb-cookie-icon-display-text.acb-cookie-icon-pos-bottom-right,
#${COOKIE_ICON_ID}.acb-cookie-icon-display-text.acb-cookie-icon-pos-top-right{
  top:auto;left:auto;bottom:0;right:8px;
}
}
#${COOKIE_ICON_ID}.acb-cookie-icon-pos-bottom-left{bottom:20px;left:20px;}
#${COOKIE_ICON_ID}.acb-cookie-icon-pos-bottom-right{bottom:20px;right:20px;}
#${COOKIE_ICON_ID}.acb-cookie-icon-pos-top-left{top:20px;left:20px;}
#${COOKIE_ICON_ID}.acb-cookie-icon-pos-top-right{top:20px;right:20px;}
@media(max-width:640px){
#${COOKIE_ICON_ID}.acb-cookie-icon-pos-bottom-left{bottom:14px;left:14px;}
#${COOKIE_ICON_ID}.acb-cookie-icon-pos-bottom-right{bottom:14px;right:14px;}
#${COOKIE_ICON_ID}.acb-cookie-icon-pos-top-left{top:14px;left:14px;}
#${COOKIE_ICON_ID}.acb-cookie-icon-pos-top-right{top:14px;right:14px;}
}
  `.trim();
}
